/**
 * A strategy's cadence — "When does it run?" (CU-869fhuhy6, brief v3 D6):
 * the fields of the old schedule rule, now the strategy's own and never a
 * tenant default. What this module holds is the *words and the rules*
 * around those fields: validation per field, the two-line summary the
 * table and the card share, the run hours for the "Runs at" text, and
 * the "align B with A" copy of criterion 29, and the "Pickup dates" line.
 *
 * Since CU-869fkr1a9 (brief criteria 16-18) a strategy has no pickup
 * horizon: the pickup dates it searches are those of "When does it
 * apply" — lead times or seasons, narrowed by pickup weekday — from today
 * to its farthest lead time or the end of its last season. They live in
 * the period (`Page/@components/periodShape`), not here.
 *
 * What it does NOT hold is any count of rate shops. The number of
 * searches a cadence launches is the engine's (`GET|POST
 * /strategies/rate-shops`, UX D5 "never a formula re-typed in the
 * front"); the prototype's browser estimate was dropped on purpose (plan
 * § 5.1, Obs. 1 of the UX gate).
 *
 * Client shape (camelCase; `Page/@services/adapters` maps it to the wire):
 *   { frequencyKind: 'every_n_hours' | 'specific_hours', frequencyValue: n | ['HH:MM'],
 *     windowStart, windowEnd, weekdays: ['mon', …], pickupHour, dropoffHour, reviewed }
 *
 * Pure module: no React, no fetch. Unit-tested with `node --test`.
 */

const WEEKDAY_CODES = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']
const WEEKDAY_SHORT = { mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun' }
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const pad2 = (n) => String(n).padStart(2, '0')

/**
 * The factory proposal a new strategy starts from (UX v3 D19): valid,
 * marked "factory proposal — review it" until a field is edited or the
 * operator says it looks right. `reviewed: false` is the mark.
 */
export const FACTORY_CADENCE = Object.freeze({
	frequencyKind: 'specific_hours', frequencyValue: ['09:00'],
	windowStart: '06:00', windowEnd: '22:00',
	weekdays: [...WEEKDAY_CODES],
	pickupHour: '11:00', dropoffHour: '11:00',
	// The 'factory proposal — review it' chip was removed (Curro, 2026-10-20: it
	// added nothing); the flag stays on the wire for the server, always true.
	reviewed: true,
})

/** A fresh, mutable copy of the factory proposal. */
export const factoryCadence = () => ({
	...FACTORY_CADENCE, weekdays: [...FACTORY_CADENCE.weekdays], frequencyValue: [...FACTORY_CADENCE.frequencyValue],
})

export const fmtDay = (d) => (d ? `${pad2(d.getDate())} ${MONTHS[d.getMonth()]} ${d.getFullYear()}` : '—')

/** `2026-12-01` → the day, as the summary wants it; anything else → `—`. */
export const fmtIsoDay = (s) => fmtDay(fromIso(s))

const fromIso = (s) => {
	const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(s || ''))
	return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null
}

/** The hours the engine fires on an active day, `HH:MM`, sorted — for the "Runs at" text and the align dialog. */
export function runHours(cadence) {
	if (!cadence) return []
	if (cadence.frequencyKind === 'specific_hours') {
		return [...(cadence.frequencyValue || [])].filter(Boolean).sort()
	}
	const [ws] = String(cadence.windowStart || '').split(':').map(Number)
	const [we] = String(cadence.windowEnd || '').split(':').map(Number)
	if (!Number.isFinite(ws) || !Number.isFinite(we)) return []
	const step = Math.max(1, Number(cadence.frequencyValue) || 1)
	const out = []
	for (let h = ws; h <= we; h += step) out.push(`${pad2(h)}:00`)
	return out
}

/** Short run-days label: `every day`, `Mon–Fri`, `Sat, Sun`, `no day`. */
export function runDaysLabel(weekdays) {
	const days = WEEKDAY_CODES.filter(c => (weekdays || []).includes(c))
	if (days.length === 7) return 'every day'
	if (days.length === 0) return 'no day'
	const idx = days.map(c => WEEKDAY_CODES.indexOf(c))
	const contiguous = idx.every((v, i) => i === 0 || v === idx[i - 1] + 1)
	if (contiguous && days.length >= 3) return `${WEEKDAY_SHORT[days[0]]}–${WEEKDAY_SHORT[days[days.length - 1]]}`
	return days.map(c => WEEKDAY_SHORT[c]).join(', ')
}

/** `+1–7, +65–68 days` · `1 Jul – 31 Aug` · null: the pickups of a period, short, for the table's second line. */
const monthDay = (md) => {
	const m = /^(\d{2})-(\d{2})$/.exec(String(md || ''))
	return m ? `${Number(m[2])} ${MONTHS[Number(m[1]) - 1]}` : '—'
}
const span = (from, to) => (Number(from) === Number(to) ? `${from}` : `${from}–${to}`)
const leadTimesOf = (period) => (period?.leadTimes || []).filter(lt => lt && lt.leadFrom != null && lt.leadTo != null)
const seasonsOf = (period) => (period?.seasons || []).filter(s => s && s.seasonFrom && s.seasonTo)

/**
 * Two lines: `Every 2 h · 06:00–22:00 · every day` / `pickups +1–7, +65–68
 * days · 12:00 / 12:00`. The second comes from the strategy's period
 * (CU-869fkr1a9): its lead times, or its seasons, or the fact that it has
 * neither.
 */
export function cadenceSummary(cadence, period = null) {
	if (!cadence) return ['—', '']
	const freq = cadence.frequencyKind === 'specific_hours'
		? `At ${(cadence.frequencyValue || []).slice().sort().join(', ') || '—'}`
		: `Every ${cadence.frequencyValue ?? '—'} h · ${cadence.windowStart || '—'}–${cadence.windowEnd || '—'}`
	const line1 = `${freq} · ${runDaysLabel(cadence.weekdays)}`
	const leads = leadTimesOf(period), seasons = seasonsOf(period)
	const pickups = leads.length
		? `pickups ${leads.map(lt => `+${span(lt.leadFrom, lt.leadTo)}`).join(', ')} days`
		: seasons.length
			? `pickups ${seasons.map(x => `${monthDay(x.seasonFrom)} – ${monthDay(x.seasonTo)}`).join(', ')}`
			: 'pickups — no lead times or seasons'
	const hours = cadence.pickupHour || cadence.dropoffHour
		? `${cadence.pickupHour || 'any'} / ${cadence.dropoffHour || 'any'}`
		: 'any hour'
	return [line1, `${pickups} · ${hours}`]
}

const ISO_WEEKDAY_SHORT = { 1: 'Mon', 2: 'Tue', 3: 'Wed', 4: 'Thu', 5: 'Fri', 6: 'Sat', 7: 'Sun' }

/** `[['2026-10-22','2026-10-28'], ['2026-12-25','2026-12-28']]` → `22–28 Oct, 25–28 Dec 2026`. */
function rangesLabel(ranges) {
	const parsed = (ranges || []).map(([a, b]) => [fromIso(a), fromIso(b || a)]).filter(([a, b]) => a && b)
	if (!parsed.length) return ''
	const years = new Set(parsed.flatMap(([a, b]) => [a.getFullYear(), b.getFullYear()]))
	const oneYear = years.size === 1
	const dm = (d) => `${d.getDate()} ${MONTHS[d.getMonth()]}`
	const withYear = (d) => `${dm(d)} ${d.getFullYear()}`
	const parts = parsed.map(([a, b]) => {
		const sameDay = a.getTime() === b.getTime()
		const sameMonth = a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth()
		if (oneYear) return sameDay ? dm(a) : sameMonth ? `${a.getDate()}–${dm(b)}` : `${dm(a)} – ${dm(b)}`
		if (sameDay) return withYear(a)
		if (sameMonth) return `${a.getDate()}–${withYear(b)}`
		return a.getFullYear() === b.getFullYear() ? `${dm(a)} – ${withYear(b)}` : `${withYear(a)} – ${withYear(b)}`
	})
	return oneYear ? `${parts.join(', ')} ${[...years][0]}` : parts.join(', ')
}

/**
 * The read-only "Pickup dates" line of When does it run (CU-869fkr1a9, UX
 * Obs. 8, 10, 12, 14): *lead times 1–7, 65–68 → 11 days (22–28 Oct, 25–28
 * Dec 2026) · every pickup weekday*. The number is the engine's
 * (`breakdown.pickup_dates`) and the dates are its `pickup_ranges`, both
 * from the same composition (TL Obs. 6). An engine without ranges: the
 * number alone; no estimate: what the period says, never blank. Null when
 * the period has neither lead times nor seasons (the card says "No pickup
 * dates").
 */
export function pickupDatesLine(period, estimate) {
	const leads = leadTimesOf(period), seasons = seasonsOf(period)
	let head
	if (leads.length) head = `lead times ${leads.map(lt => span(lt.leadFrom, lt.leadTo)).join(', ')}`
	else if (seasons.length) head = `${seasons.length === 1 ? 'season' : 'seasons'} ${seasons.map(x => `${monthDay(x.seasonFrom)} – ${monthDay(x.seasonTo)}`).join(', ')}`
	else return null
	const n = estimate?.breakdown?.pickup_dates
	const ranges = rangesLabel(estimate?.pickup_ranges)
	const count = n != null ? ` → ${fmtInt(n)} day${n === 1 ? '' : 's'}${ranges ? ` (${ranges})` : ''}` : ''
	const days = (period?.weekdays || []).map(Number).filter(d => ISO_WEEKDAY_SHORT[d])
	const weekdays = !days.length || days.length === 7 ? 'every pickup weekday' : [...new Set(days)].sort((a, b) => a - b).map(d => ISO_WEEKDAY_SHORT[d]).join(', ')
	return `${head}${count} · ${weekdays}`
}

/**
 * The cadence's own validation, as the schedule rule had it
 * (`Schedule/Rules/Drawer.jsx:62-83`), per field so the message can sit
 * under the field (UX D4). `{}` when valid.
 */
export function cadenceErrors(cadence) {
	const e = {}
	if (!cadence) return e
	if (cadence.frequencyKind === 'specific_hours') {
		if (!(cadence.frequencyValue || []).length) e.frequency = 'Add at least one specific hour.'
	} else {
		if (!(Number(cadence.frequencyValue) >= 1)) e.frequency = 'Every how many hours? At least 1.'
		if (!cadence.windowStart || !cadence.windowEnd) e.window = 'The run window needs two complete times (HH:MM).'
		else if (cadence.windowStart >= cadence.windowEnd) e.window = 'The run window must start before it ends.'
	}
	if (!(cadence.weekdays || []).length) e.weekdays = 'Select at least one day it runs.'
	return e
}

/** `04:00` · `06:00, 08:00, 10:00` · `06:00 … 22:00 (9 runs)`. */
export function hoursLabel(hours) {
	const h = [...(hours || [])].sort()
	if (!h.length) return 'no hour'
	if (h.length <= 3) return h.join(', ')
	return `${h[0]} … ${h[h.length - 1]} (${h.length} runs)`
}

/**
 * `target` running when `source` runs (criterion 29, UX v3 D21):
 * frequency, window, days and hour list copied; the pickup / dropoff time
 * of the rental kept. The result is the strategy's own
 * cadence, reviewed — somebody chose it.
 */
export function alignHours(target, source) {
	return {
		...target,
		frequencyKind: source.frequencyKind,
		frequencyValue: Array.isArray(source.frequencyValue) ? [...source.frequencyValue] : source.frequencyValue,
		windowStart: source.windowStart,
		windowEnd: source.windowEnd,
		weekdays: Array.isArray(source.weekdays) ? [...source.weekdays] : target.weekdays,
		reviewed: true,
	}
}

export const NO_PICKUP_DATES = 'no pickup date of When does it apply falls in the searched days'

/**
 * Why the engine counts zero searches, as one clause for the rate-shop
 * line (CU-869fkr1a9). The engine sends `reason` — a skip key or a short
 * text — and `skipped` (`pms-adapter … sweep/runner.py` `estimate`). The
 * line used to blame the period for every zero; on canarias.com the cause
 * was a product whose rows had no brackets saved. A row without brackets
 * and a range that holds no bracket's reference are two different keys
 * (TL R3); an unknown key reads as itself.
 */
const ZERO_REASONS = {
	no_tier_covers_the_duration: 'the product\u2019s rows have no rental-length brackets saved — open Setup › Products and save the product',
	duration_has_no_reference_lor: 'no bracket\u2019s reference length falls in its rental lengths',
	strategy_without_pickup_dates: 'it has no lead times and no season dates — set one in Dates & LOR, card 1',
	strategy_excludes_every_office: 'this strategy excludes every office — nothing to search',
	strategy_covers_none_of_its_dates: 'its pickup weekdays leave no date of its lead times or seasons — widen them in Dates & LOR, card 1',
}

export function zeroSearchesReason(e) {
	const key = e?.reason || Object.entries(e?.skipped || {}).find(([, n]) => n)?.[0]
	if (!key) return NO_PICKUP_DATES
	return ZERO_REASONS[key] || String(key).replace(/_/g, ' ')
}

export const fmtInt = (n) => new Intl.NumberFormat('en-GB').format(n || 0)
export { WEEKDAY_CODES, WEEKDAY_SHORT }
